import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  Trash2, Eye, FileImage, User as UserIcon, Shield
} from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

interface User {
  id: string;
  full_name: string;
  email: string;
  role: string;
  kyc_status: string;
  created_at: string;
  phone?: string;
  national_id?: string;
  gender?: string;
  address_country?: string;
  address_city?: string;
  address_area?: string;
  address_street?: string;
  address_building?: string;
  address_apt?: string;
  id_front_url?: string;
  id_back_url?: string;
  avatar_url?: string;
  language?: string;
  university_id?: string;
  budget_max?: number;
  roommates_count?: number;
  expected_move_in?: string;
  property_ownership_type?: string;
  payout_method?: string;
  updated_at?: string;
}

interface UserTableViewProps {
  users: User[];
  onViewUser: (user: User) => void;
  onUpdateRole: (userId: string, newRole: string) => void;
  onDeleteUser: (userId: string) => void;
  isDeleting: boolean;
}

const UserTableView = ({ users, onViewUser, onUpdateRole, onDeleteUser, isDeleting }: UserTableViewProps) => {
  const { t } = useLanguage();

  const formatAddress = (user: User) => {
    const parts = [
      user.address_city,
      user.address_country
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : 'Not provided';
  };

  const viewDocument = (url: string) => {
    if (url) {
      window.open(url, '_blank');
    }
  };

  const getRoleBadgeVariant = (role: string) => {
    switch (role) {
      case 'admin': return 'destructive';
      case 'landlord': return 'default';
      case 'student': return 'secondary';
      default: return 'outline';
    }
  };

  const getKYCBadgeVariant = (status: string) => {
    switch (status) {
      case 'VERIFIED': return 'default';
      case 'PENDING': return 'secondary';
      case 'REJECTED': return 'destructive';
      case 'UNDER_REVIEW': return 'outline';
      default: return 'outline';
    }
  };

  if (users.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        {t('no_users_found', 'لم يتم العثور على مستخدمين', 'No users found')}
      </div>
    );
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('user', 'المستخدم', 'User')}</TableHead>
            <TableHead>{t('contact', 'الاتصال', 'Contact')}</TableHead>
            <TableHead>{t('location', 'الموقع', 'Location')}</TableHead>
            <TableHead>{t('role', 'الدور', 'Role')}</TableHead>
            <TableHead>{t('kyc_status', 'حالة التحقق', 'KYC Status')}</TableHead>
            <TableHead>{t('documents', 'الوثائق', 'Documents')}</TableHead>
            <TableHead>{t('joined', 'انضم في', 'Joined')}</TableHead>
            <TableHead className="text-right">{t('actions', 'الإجراءات', 'Actions')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {users.map((user) => (
            <TableRow key={user.id} className="hover:bg-muted/50">
              <TableCell>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserIcon className="h-4 w-4 text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-sm truncate">{user.full_name || 'Unnamed User'}</div>
                    <div className="text-xs text-muted-foreground truncate">{user.email}</div>
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-sm">
                <div className="space-y-1">
                  {user.phone && <div className="truncate">{user.phone}</div>}
                  {user.national_id && <div className="text-xs text-muted-foreground">***{user.national_id.slice(-4)}</div>}
                </div>
              </TableCell>
              <TableCell className="text-sm">
                <div className="space-y-1">
                  <div className="truncate">{formatAddress(user)}</div>
                  {user.language && <div className="text-xs text-muted-foreground capitalize">{user.language}</div>}
                </div>
              </TableCell>
              <TableCell>
                <Select
                  value={user.role}
                  onValueChange={(newRole) => onUpdateRole(user.id, newRole)}
                >
                  <SelectTrigger className="w-24 h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="student">{t('student', 'طالب', 'Student')}</SelectItem>
                    <SelectItem value="landlord">{t('landlord', 'مالك', 'Landlord')}</SelectItem>
                    <SelectItem value="admin">{t('admin', 'مدير', 'Admin')}</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell>
                <Badge variant={getKYCBadgeVariant(user.kyc_status)} className="text-xs">
                  {user.kyc_status}
                </Badge>
              </TableCell>
              <TableCell>
                <div className="flex gap-1">
                  {user.id_front_url && (
                    <button 
                      className="p-1 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded hover:bg-green-100 dark:hover:bg-green-900 transition-colors"
                      onClick={() => viewDocument(user.id_front_url!)}
                      title={t('front_id', 'الهوية الأمامية', 'Front ID')}
                    >
                      <FileImage className="h-3 w-3 text-green-600" />
                    </button>
                  )}
                  {user.id_back_url && (
                    <button 
                      className="p-1 bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded hover:bg-green-100 dark:hover:bg-green-900 transition-colors"
                      onClick={() => viewDocument(user.id_back_url!)}
                      title={t('back_id', 'الهوية الخلفية', 'Back ID')}
                    >
                      <FileImage className="h-3 w-3 text-green-600" />
                    </button>
                  )}
                </div>
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {new Date(user.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex gap-1 justify-end">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onViewUser(user)}
                    className="h-7 px-2"
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-red-600 hover:text-red-700 h-7 px-2"
                        disabled={isDeleting}
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>
                          {t('delete_user', 'حذف المستخدم', 'Delete User')}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('delete_user_confirm', 
                            'هل أنت متأكد من حذف هذا المستخدم؟ هذا الإجراء لا يمكن التراجع عنه.',
                            `Are you sure you want to delete ${user.full_name}? This action cannot be undone and will permanently remove the user from both the website and database.`
                          )}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>
                          {t('cancel', 'إلغاء', 'Cancel')}
                        </AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => onDeleteUser(user.id)}
                          className="bg-red-600 hover:bg-red-700"
                          disabled={isDeleting}
                        >
                          {isDeleting ? 'Deleting...' : t('delete', 'حذف', 'Delete')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default UserTableView;